import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ArrowRight, Laptop, Moon, Sun } from 'lucide-react';
import { useSearchState, useThemeState } from '../stores/ui-store';
import { buildCommandSections, flattenCommandEntries, type CommandEntry } from './command-entries';
import type { NavGroup, SidebarView } from './layout/types';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import { ScrollArea } from './ui/scroll-area';

/**
 * Paleta de comenzi (⌘K / Ctrl+K).
 *
 * Faţă de shadcn-admin, unde citeşte direct din `sidebar-data`, primeşte navigaţia prin
 * props — aceleaşi grupuri **şi aceleaşi vederi** filtrate după permisiuni pe care le vede
 * şi sidebar-ul. Altfel paleta ar fi o portiţă către ecrane pe care meniul le ascunde.
 *
 * Aplatizarea (prefixare, dedublare pe url, textul de căutat) stă în `command-entries.ts`
 * şi are teste proprii.
 */

const MODIFIER_KEYS = ['Shift', 'Meta', 'Control', 'Alt'];
const SEQUENCE_RESET_MS = 500;
const SEQUENCE_INVOKE_MS = 250;

function keysMatch(shortcut: string[], keys: string[]) {
  return (
    shortcut.length === keys.length &&
    shortcut.every((key, index) => key.toLowerCase() === keys[index]?.toLowerCase())
  );
}

function isPrefixOf(shortcut: string[], keys: string[]) {
  return (
    shortcut.length > keys.length &&
    keys.every((key, index) => key.toLowerCase() === shortcut[index]?.toLowerCase())
  );
}

/**
 * Navigare globală „G apoi X", portată din edinro (care a luat-o din keybind-ul Medusa):
 * tastele se adună într-o secvenţă, care se resetează după o pauză scurtă și e ignorată
 * cât timp cursorul e într-un input, textarea sau element editabil.
 *
 * Ramura „exact şi extensibil" n-a fost exersată de nimic până la #76: `G S` duce la
 * Setări, dar `G S P` la Pachete, deci după `G S` se aşteaptă scurt următoarea tastă. E
 * preţul scurtăturilor de submeniu, plătit pe prefixul lor comun.
 */
function useNavShortcuts(entries: CommandEntry[], onNavigate: (url: string) => void) {
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  useEffect(() => {
    let keys: string[] = [];
    let resetTimer: number | undefined;
    let invokeTimer: number | undefined;

    const reset = () => {
      keys = [];
    };

    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        reset();
        return;
      }

      if (MODIFIER_KEYS.includes(event.key)) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) {
        reset();
        return;
      }

      window.clearTimeout(resetTimer);
      window.clearTimeout(invokeTimer);

      keys = [...keys, event.key];

      const candidates = entriesRef.current.filter(
        (entry): entry is CommandEntry & { shortcut: string[] } => Boolean(entry.shortcut?.length),
      );
      const exact = candidates.find((entry) => keysMatch(entry.shortcut, keys));
      const extendable = candidates.some((entry) => isPrefixOf(entry.shortcut, keys));

      if (exact && !extendable) {
        reset();
        onNavigateRef.current(exact.url);
        return;
      }

      if (exact && extendable) {
        // O scurtătură mai lungă începe la fel — aşteaptă scurt următoarea tastă.
        invokeTimer = window.setTimeout(() => {
          reset();
          onNavigateRef.current(exact.url);
        }, SEQUENCE_INVOKE_MS);
      } else if (!extendable) {
        reset();
        return;
      }

      resetTimer = window.setTimeout(reset, SEQUENCE_RESET_MS);
    };

    window.addEventListener('keydown', listener);

    return () => {
      window.removeEventListener('keydown', listener);
      window.clearTimeout(resetTimer);
      window.clearTimeout(invokeTimer);
    };
  }, []);
}

/**
 * `G` `S`, taste separate — nu textul „G+S".
 *
 * E o **secvenţă**, nu o combinaţie: se apasă una după alta. Două căsuţe alăturate spun
 * asta; un plus între ele ar spune exact pe dos.
 */
function ShortcutKeys({ keys }: { keys: string[] }) {
  return (
    <CommandShortcut className="flex items-center gap-1">
      {keys.map((key, index) => (
        <kbd
          key={`${key}-${index}`}
          className="flex h-5 min-w-5 items-center justify-center rounded border bg-muted px-1 font-mono text-[10px] font-medium tracking-normal"
        >
          {key}
        </kbd>
      ))}
    </CommandShortcut>
  );
}

type CommandMenuProps = {
  groups: NavGroup[];
  /** Vederile drill-down ale sidebar-ului, deja filtrate pe permisiuni. */
  views?: SidebarView[];
  onNavigate: (url: string) => void;
};

export function CommandMenu({ groups, views, onNavigate }: CommandMenuProps) {
  const { setTheme } = useThemeState();
  const { open, setOpen } = useSearchState();

  const sections = useMemo(() => buildCommandSections(groups, views), [groups, views]);
  const entries = useMemo(() => flattenCommandEntries(sections), [sections]);
  useNavShortcuts(entries, onNavigate);

  const runCommand = useCallback(
    (command: () => unknown) => {
      setOpen(false);
      command();
    },
    [setOpen],
  );

  return (
    <CommandDialog modal open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Caută o pagină sau o comandă…" />
      <CommandList>
        <ScrollArea type="hover" className="h-72 pe-1">
          <CommandEmpty>Niciun rezultat.</CommandEmpty>
          {sections.map((section) => (
            <CommandGroup key={section.key} heading={section.heading}>
              {section.entries.map((entry) => (
                <CommandItem
                  key={entry.key}
                  value={entry.value}
                  onSelect={() => runCommand(() => onNavigate(entry.url))}
                >
                  <div className="flex size-4 items-center justify-center">
                    <ArrowRight className="size-2 text-muted-foreground/80" />
                  </div>
                  {entry.label}
                  {entry.shortcut?.length ? <ShortcutKeys keys={entry.shortcut} /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          <CommandSeparator />
          <CommandGroup heading="Temă">
            <CommandItem onSelect={() => runCommand(() => setTheme('light'))}>
              <Sun /> <span>Luminoasă</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme('dark'))}>
              <Moon className="scale-90" />
              <span>Întunecată</span>
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme('system'))}>
              <Laptop />
              <span>Sistem</span>
            </CommandItem>
          </CommandGroup>
        </ScrollArea>
      </CommandList>
    </CommandDialog>
  );
}
